export const createApprovalLineState = () => ({
  depHeads: [], // 중간결재자 (최대 4명)
  safetyManager: null, // 최종결재자 (1명)
  observer: null, // 입회자 (1명)
  smId: '', // 최종결재자 ID 연동용
  initialized: false,
  touched: false,
});

function pickField(row, keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
}

function resolveRowId(row = {}) {
  return pickField(row, ['id', 'Id', 'userId', 'userid', 'decUserId']);
}

function isExternalMember(row = {}) {
  return pickField(row, ['internalYn', 'INTERNAL_YN']) === '1';
}

function resolveDepGroup(row = {}) {
  if (isExternalMember(row)) {
    return { depCd: 'EXTERNAL', depNm: '외부인' };
  }

  const depCd = pickField(row, ['depCd', 'DEP_CD']) || 'UNKNOWN';
  const depNm =
    pickField(row, ['depNmLast', 'depNm', 'DEP_NM_LAST']) || '외부인';

  return { depCd, depNm };
}

function resolveMemberDepNm(row = {}) {
  if (isExternalMember(row)) {
    return pickField(row, ['affiliationNm', 'AFFILIATION_NM']) || '외부인';
  }

  return pickField(row, ['depNmLast', 'depNm', 'DEP_NM_LAST']);
}

function resolveMemberCompany(row = {}) {
  if (!isExternalMember(row)) return '';
  return pickField(row, ['affiliationNm', 'AFFILIATION_NM']);
}

function resolveMemberRank(row = {}) {
  return pickField(row, ['rank', 'RANK']);
}

function resolveMemberPosition(row = {}) {
  return pickField(row, ['position', 'POSITION', 'posNm']);
}

function formatMemberDuty(rank = '', position = '') {
  return [rank, position].filter(Boolean).join(' · ');
}

/** depApproval / 후보 목록 / userInfo / 최근결재 row에서 담당자 프로필 조회 */
export function resolveManagerProfile({
  id,
  managers = [],
  candidateRows = [],
  userInfo = {},
  sourceRow = null,
} = {}) {
  const targetId = String(id ?? '');
  if (!targetId) return null;

  const fromManagers = managers.find((item) => String(item.id) === targetId);
  if (fromManagers?.name) {
    const rank = fromManagers.rank || '';
    const jobPosition = fromManagers.jobPosition || fromManagers.position || '';

    return {
      id: targetId,
      name: fromManagers.name,
      rank,
      jobPosition,
      position: formatMemberDuty(rank, jobPosition),
      photo: fromManagers.photo || '',
      depCd: fromManagers.depCd || '',
      depNm: fromManagers.companyNm || fromManagers.depNm || '',
    };
  }

  const fromCandidates = (candidateRows || []).find(
    (row) => resolveRowId(row) === targetId
  );
  if (fromCandidates) {
    const name = pickField(fromCandidates, ['name', 'userNm', 'USER_NM']);
    if (name) {
      const rank = resolveMemberRank(fromCandidates);
      const jobPosition = resolveMemberPosition(fromCandidates);

      return {
        id: targetId,
        name,
        rank,
        jobPosition,
        position: formatMemberDuty(rank, jobPosition),
        photo: pickField(fromCandidates, ['photo']),
        depCd: pickField(fromCandidates, ['depCd', 'DEP_CD']),
        depNm: resolveMemberDepNm(fromCandidates),
      };
    }
  }

  const myId = String(userInfo.decUserId || userInfo.userid || '');
  if (myId && myId === targetId) {
    const rank = userInfo.rank || '';
    const jobPosition = userInfo.posiNm || userInfo.position || '';

    return {
      id: targetId,
      name: userInfo.userNm || '',
      rank,
      jobPosition,
      position: formatMemberDuty(rank, jobPosition),
      photo: userInfo.photo || '',
      depCd: userInfo.depCd || '',
      depNm: userInfo.depNm || '',
    };
  }

  if (sourceRow) {
    const name = pickField(sourceRow, [
      'name',
      'userNm',
      'approverNm',
      'USER_NM',
    ]);
    if (name) {
      const rank = resolveMemberRank(sourceRow);
      const jobPosition = resolveMemberPosition(sourceRow);

      return {
        id: targetId,
        name,
        rank,
        jobPosition,
        position: formatMemberDuty(rank, jobPosition),
        photo: pickField(sourceRow, ['photo']),
        depCd: pickField(sourceRow, ['depCd', 'DEP_CD']),
        depNm: pickField(sourceRow, ['depNm', 'depNmLast', 'DEP_NM_LAST']),
      };
    }
  }

  if (fromManagers) {
    const rank = fromManagers.rank || '';
    const jobPosition = fromManagers.jobPosition || fromManagers.position || '';

    return {
      id: targetId,
      name: fromManagers.name || '',
      rank,
      jobPosition,
      position: formatMemberDuty(rank, jobPosition),
      photo: fromManagers.photo || '',
      depCd: fromManagers.depCd || '',
      depNm: fromManagers.companyNm || fromManagers.depNm || '',
    };
  }

  return {
    id: targetId,
    name: '',
    rank: '',
    jobPosition: '',
    position: '',
    photo: '',
    depCd: '',
    depNm: '',
  };
}

/** GetPerCustomR4 결과를 부서별 결재/입회 후보 목록으로 변환 (입회자 선택 시 본인 포함) */
export const buildDepApprovalFromRows = (rows = [], userInfo = {}) => {
  const grouped = (rows || []).reduce((acc, curr) => {
    const { depCd, depNm } = resolveDepGroup(curr);

    if (!acc[depCd]) {
      acc[depCd] = {
        depCd,
        depNm,
        managers: [],
      };
    }

    const memberId = resolveRowId(curr);
    if (!memberId) return acc;

    const alreadyListed = acc[depCd].managers.some(
      (manager) => String(manager.id) === memberId
    );
    if (alreadyListed) return acc;

    acc[depCd].managers.push({
      id: memberId,
      name: pickField(curr, ['name', 'userNm', 'USER_NM']),
      rank: resolveMemberRank(curr),
      position: resolveMemberPosition(curr),
      photo: pickField(curr, ['photo']),
      tel: pickField(curr, ['tel', 'phone']),
      companyNm: resolveMemberCompany(curr),
    });

    return acc;
  }, {});

  const myId = String(userInfo.decUserId || userInfo.userid || '');
  const myDepCd = userInfo.depCd || 'UNKNOWN';

  if (myId) {
    if (!grouped[myDepCd]) {
      grouped[myDepCd] = {
        depCd: myDepCd,
        depNm: userInfo.depNm || '외부인',
        managers: [],
      };
    }

    const alreadyListed = grouped[myDepCd].managers.some(
      (manager) => String(manager.id) === myId
    );

    if (!alreadyListed) {
      grouped[myDepCd].managers.unshift({
        id: myId,
        name: userInfo.userNm,
        rank: userInfo.rank || '',
        position: userInfo.posiNm || userInfo.position || '',
        photo: userInfo.photo,
        tel: userInfo.tel || '',
        companyNm: '',
      });
    }
  }

  return Object.values(grouped);
};

export const normalizeApprovalLine = (
  rows = [],
  depApproval = [],
  { userInfo = {}, candidateRows = [] } = {}
) => {
  const managers = depApproval.flatMap((dep) =>
    (dep.managers || []).map((manager) => ({
      ...manager,
      depCd: dep.depCd,
      depNm: manager.companyNm || dep.depNmLast || dep.depNm,
    }))
  );

  const lookupOptions = { managers, candidateRows, userInfo };

  const depHeads = rows
    .filter((row) => row.approverType === 'INTERMEDIATE')
    .sort((a, b) => (a.no || 0) - (b.no || 0))
    .map((row) => {
      const profile = resolveManagerProfile({
        ...lookupOptions,
        id: row.id,
        sourceRow: row,
      });

      return {
        depCd: profile?.depCd || '',
        depNm: profile?.depNm || '',
        headId: row.id,
        headNm: profile?.name || '',
        rank: profile?.rank || '',
        jobPosition: profile?.jobPosition || '',
        position: profile?.position || '',
        photo: profile?.photo || '',
        no: row.no || 0,
      };
    });

  const finalRow = rows
    .filter((row) => row.approverType === 'FINAL')
    .sort((a, b) => (a.no || 0) - (b.no || 0))[0];
  const finalProfile = finalRow
    ? resolveManagerProfile({
        ...lookupOptions,
        id: finalRow.id,
        sourceRow: finalRow,
      })
    : null;

  const safetyManager = finalRow
    ? {
        depCd: finalProfile?.depCd || '',
        depNm: finalProfile?.depNm || '',
        smId: finalRow.id,
        smNm: finalProfile?.name || '',
        rank: finalProfile?.rank || '',
        jobPosition: finalProfile?.jobPosition || '',
        position: finalProfile?.position || '',
        photo: finalProfile?.photo || '',
        no: finalRow.no || 0,
      }
    : null;

  const observerRow = rows.find((row) => row.approverType === 'WITNESS');
  const witnessProfile = observerRow
    ? resolveManagerProfile({
        ...lookupOptions,
        id: observerRow.id,
        sourceRow: observerRow,
      })
    : null;

  const observer = observerRow
    ? {
        depCd: witnessProfile?.depCd || '',
        depNm: witnessProfile?.depNm || '',
        id: observerRow.id,
        name: witnessProfile?.name || '',
        rank: witnessProfile?.rank || '',
        jobPosition: witnessProfile?.jobPosition || '',
        position: witnessProfile?.position || '',
        photo: witnessProfile?.photo || '',
      }
    : null;

  return {
    depHeads,
    safetyManager,
    observer,
    smId: safetyManager?.smId || '',
  };
};
